import * as React from 'react';
import * as Sebrae from '@sebrae/react-core';
import {
  BaSeBreakpointContext,
  BaSeContainer,
  BaSeHeading3,
  BaSeHeading5,
  BaSeLoading,
  BaSeParagraph,
  BaSeTheme,
} from '@base/react';
import {
  colunas,
  RelatoriosLancamentos,
} from '../new-relatorios/relatorios-lancamentos';
import { RelatorioFluxoLista } from '../../components/new-relatorios/fluxo/relatorio-fluxo-lista';
import { RelatorioExtratoLista } from '../../components/new-relatorios/extrato/relatorio-extrato-lista';
import { RelatorioDiarioLista } from '../../components/new-relatorios/diario/relatorio-diario-lista.jsx';
import { ReactComponent as InvalidLink } from '../../images/invalid-link.svg';
import {
  CenterComponent,
  CenterGrid,
  ContentTitre,
  WarapperTextContent,
  WrapperInfo,
} from './relatorios-compartilhados-styled.js';
import { RelatoriosMei } from '../new-relatorios/relatorios-mei.jsx';
import { useRelatorio } from '../../services/relatorio/RelatorioService.js';

export const RelatoriosCompartilhado = () => {
  const [lancamentosRelatorio, setLancamentosRelatorio] = React.useState(null);
  const [isError, setIsError] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  const { relatorioPublico } = useRelatorio();
  const { getRouteParam } = Sebrae.useRouter();
  const { inTablet } = React.useContext(BaSeBreakpointContext);

  const { azulSebrae36, cinzaSebrae20, cinzaSebrae30 } =
    BaSeTheme.colors.institucionais;
  const keyRelatorioPublico = getRouteParam('link');

  const typeReportMei = React.useMemo(
    () => ({
      RELATORIO_DASN: 'Relatório DASN MEI',
      RELATORIO_MENSAL_MEI: 'Relatório Mensal MEI',
    }),
    [],
  );

  const typeReport = React.useMemo(
    () => ({
      RELATORIO_DIARIO: 'Diário',
      RELATORIO_EXTRATO: 'Extrato',
      RELATORIO_FLUXO: 'Fluxo de caixa',
      RELATORIO_CONTATO: 'Relatório por contato',
      RELATORIO_CATEGORIA: 'Relatório por categoria',
      RELATORIO_CONTA: 'Relatório por conta',
      RELATORIO_DASN: 'Relatório DASN MEI',
      RELATORIO_MENSAL_MEI: 'Relatório Mensal MEI',
    }),
    [],
  );

  const camposPersonalizados = React.useMemo(() => {
    return colunas?.filter((coluna) =>
      lancamentosRelatorio?.relatorio?.campoCustomizado?.includes(coluna?.tipo),
    );
  }, [lancamentosRelatorio]);

  const relatorioPorTipo = React.useMemo(
    () => (
      <RelatoriosLancamentos
        lancamentosRelatorio={lancamentosRelatorio?.relatorio}
        nomeRelatorio={typeReport[lancamentosRelatorio?.tipoRelatorio]}
        startDateBroken={/*!isFirstDayOfMonth(startDate)*/ false}
        endDateBroken={/*!isLastDayOfMonth(endDate)*/ false}
        typeReport={typeReport}
        colunasPersonalizadas={camposPersonalizados}
        isPublic={true}
      />
    ),
    [camposPersonalizados, lancamentosRelatorio, typeReport],
  );

  const relatoriosMEI = React.useMemo(
    () => (
      <RelatoriosMei
        dados={lancamentosRelatorio}
        tipo={typeReportMei[lancamentosRelatorio?.tipoRelatorio]}
        isPublic={true}
      />
    ),
    [lancamentosRelatorio, typeReportMei],
  );

  const visualizarRelatorio = React.useMemo(
    () => ({
      RELATORIO_DIARIO: (
        <RelatorioDiarioLista
          dados={lancamentosRelatorio?.relatorio?.lancamentos}
          totalDespesa={lancamentosRelatorio?.relatorio?.totalDespesas}
          totalReceita={lancamentosRelatorio?.relatorio?.totalReceitas}
          original={lancamentosRelatorio?.relatorio}
          isPublic={true}
        />
      ),
      RELATORIO_EXTRATO: (
        <RelatorioExtratoLista
          lancamentos={lancamentosRelatorio?.lancamentos}
          saldoAnterior={lancamentosRelatorio?.saldoAnterior}
          totalReceitas={lancamentosRelatorio?.totalReceitas}
          totalDespesas={lancamentosRelatorio?.totalDespesas}
          saldoPeriodo={lancamentosRelatorio?.saldoPeriodo}
          saldoFinal={lancamentosRelatorio?.saldoFinal}
          isPublic={true}
        />
      ),
      RELATORIO_FLUXO: (
        <RelatorioFluxoLista
          lancamentos={lancamentosRelatorio?.relatorio}
          startDateBroken={false}
          endDateBroken={false}
          isPublic={true}
        />
      ),
      RELATORIO_CONTATO: relatorioPorTipo,
      RELATORIO_CATEGORIA: relatorioPorTipo,
      RELATORIO_CONTA: relatorioPorTipo,
      RELATORIO_DASN: relatoriosMEI,
      RELATORIO_MENSAL_MEI: relatoriosMEI,
    }),
    [lancamentosRelatorio, relatorioPorTipo, relatoriosMEI],
  );

  React.useEffect(() => {
    setIsLoading(true);
    relatorioPublico(keyRelatorioPublico)
      .then(setLancamentosRelatorio)
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  }, [keyRelatorioPublico, relatorioPublico]);

  return (
    <BaSeContainer delimitedOnBreakpoint="desktop">
      {isLoading && (
        <CenterComponent>
          <BaSeLoading
            description=""
            lightMode="default"
            shadowLevel={0}
            spinnerSize={24}
            text="Carregando relatório…"
          />
        </CenterComponent>
      )}
      {isError && !isLoading && (
        <CenterComponent>
          <WarapperTextContent>
            <InvalidLink />
            <BaSeHeading5 isBold color={cinzaSebrae30}>
              O link para o relatório está expirado ou é inválido.
            </BaSeHeading5>
            <CenterGrid>
              <BaSeParagraph color={cinzaSebrae30}>
                Verifique com o responsável pelo envio se o relatório ainda está
                disponível
              </BaSeParagraph>
              <BaSeParagraph color={cinzaSebrae30}>
                Caso precise de acesso novamente, solicite um novo link.
              </BaSeParagraph>
            </CenterGrid>
          </WarapperTextContent>
        </CenterComponent>
      )}
      {lancamentosRelatorio && !isLoading && !isError && (
        <>
          <ContentTitre>
            <WrapperInfo>
              <BaSeHeading3 color={azulSebrae36}>
                {lancamentosRelatorio?.nomeEmpresa}
              </BaSeHeading3>
              <BaSeParagraph color={cinzaSebrae20}>{`CNPJ: ${
                lancamentosRelatorio?.cnpj ?? ''
              }`}</BaSeParagraph>
              <BaSeParagraph color={cinzaSebrae20}>{`Razão social: ${
                lancamentosRelatorio?.razaoSocial ?? ''
              }`}</BaSeParagraph>
              {!inTablet && (
                <BaSeParagraph color={cinzaSebrae20}>{`Atualizado em: ${
                  lancamentosRelatorio?.dataAtualizacao ?? ''
                }`}</BaSeParagraph>
              )}
            </WrapperInfo>
            {inTablet && (
              <BaSeParagraph color={cinzaSebrae20}>{`Atualizado em: ${
                lancamentosRelatorio?.dataAtualizacao ?? ''
              }`}</BaSeParagraph>
            )}
          </ContentTitre>
          {visualizarRelatorio?.[lancamentosRelatorio?.tipoRelatorio]}
        </>
      )}
    </BaSeContainer>
  );
};
